import * as pc from 'playcanvas';
import { ZSTD_GLUE_BASE64, ZSTD_WASM_BASE64 } from './zstd-decoder-data';

/**
 * ZSTD decoder module registration for the SPZ gaussian splat parser.
 *
 * PlayCanvas's official SPZ parser resolves its ZSTD decompressor through the engine's WasmModule registry
 * ('ZstdDecoderModule'). By default we register the module from the wasm binary embedded in
 * this bundle (see zstd-decoder-data.ts), served to the engine via blob URLs, so SPZ scenes
 * load on every surface (editor, discover, shared links, self-hosted exports) with no extra
 * files to host. Host apps can override with their own hosted files by either calling
 * pc.WasmModule.setConfig('ZstdDecoderModule', ...) before creating the viewer, or passing
 * explicit URLs here.
 */
export interface ZstdWasmUrls {
    /** URL of the WasmModule-conformant glue script. */
    glueUrl: string;
    /** URL of the zstd decompression wasm binary. */
    wasmUrl: string;
}

let embeddedModuleRegistered = false;

function base64ToBlobUrl(base64: string, mimeType: string): string {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

/**
 * Ensure the 'ZstdDecoderModule' wasm module is registered with the engine.
 * No-op when the host app already registered a config (its choice wins).
 */
export function ensureZstdDecoderModule(overrideUrls?: ZstdWasmUrls): void {
    try {
        if (pc.WasmModule.getConfig('ZstdDecoderModule')) {
            return; // Host app already configured the decoder — respect it
        }
        if (overrideUrls?.glueUrl && overrideUrls?.wasmUrl) {
            pc.WasmModule.setConfig('ZstdDecoderModule', {
                glueUrl: overrideUrls.glueUrl,
                wasmUrl: overrideUrls.wasmUrl,
            });
            console.log('[StorySplat Viewer] ZSTD decoder configured from provided URLs');
            return;
        }
        if (embeddedModuleRegistered) {
            return;
        }
        const glueUrl = base64ToBlobUrl(ZSTD_GLUE_BASE64, 'text/javascript');
        const wasmUrl = base64ToBlobUrl(ZSTD_WASM_BASE64, 'application/wasm');
        pc.WasmModule.setConfig('ZstdDecoderModule', { glueUrl, wasmUrl });
        embeddedModuleRegistered = true;
        console.log('[StorySplat Viewer] ZSTD decoder configured from embedded module (SPZ support)');
    } catch (err) {
        // Non-fatal: only SPZ loading depends on the decoder
        console.warn('[StorySplat Viewer] Failed to configure ZSTD decoder module:', err);
    }
}
